import lodash from 'lodash';
import moment from 'moment';
import mongoose from 'mongoose';
import api from '../../../App/socket';
import { checkIsLeadManageDeliveryRequested } from '../utils';

const validSortFields = new Set(['created_date']);
const validSortDirections = new Set([-1, 1]);

export const getLeads = (initialWhere, pagination, isAutoPrice, sortField = 'created_date', sortDirection = -1) => {
    const isDeliveryRequested = checkIsLeadManageDeliveryRequested();
    const validatedSortField = validSortFields.has(sortField) ? sortField : 'created_date';
    const validatedSortDirection = validSortDirections.has(sortDirection) ? sortDirection : -1;

    let where = { ...initialWhere };
    let order = {
        [validatedSortField]: validatedSortDirection,
    };

    if (isAutoPrice) {
        order = {
            [validatedSortField]: validatedSortDirection,
            last_valuation_date: -1,
            latest_margin: -1,
        };
    }

    if (isDeliveryRequested) {
        order = {
            transport_requested_date: -1,
        };
    }

    if (!lodash.isPlainObject(where)) {
        where = {};
    }

    const maxId = mongoose.Types.ObjectId.createFromTime(moment().subtract(10, 'minutes').valueOf() / 1000);

    if (!isDeliveryRequested) {
        if (where._id) {
            where._id = {
                $lt: maxId,
                $eq: where._id,
            };
        } else {
            where._id = {
                $lt: maxId,
            };
        }
    }

    return api.send({
        api: 'db',
        model: 'Lead',
        action: 'Read',
        options: {
            attributes: ['served_live_price', 'transport_requested_date', 'use_live_price', 'is_manager_special', 'trade.is_trade', 'lead_pot', 'photos.picture', 'has_completed_blemish', 'completed_blemish_step', 'created_date', 'valuations.created_date', 'valuations.valuation', 'valuations.retail_price', 'valuations.suggested_margin', 'marketing.ref_lead_affiliate', 'is_part_complete', 'is_duplicate', 'part_ex.is_part_ex'],
            limit: pagination.rows_per_page,
            offset: pagination.rows_per_page * pagination.page,
            order,
            include: [
                {
                    model: 'Bike_lead',
                    path: 'ref_bike_lead',
                    attributes: ['mileage', 'vrm', 'unmaped_data.bike_model', 'unmaped_data.bike_cc', 'unmaped_data.bike_year'],
                    include: [
                        {
                            model: 'Bike_brand',
                            path: 'ref_bike_brand',
                            attributes: ['name'],
                        },
                        {
                            model: 'Bike_model',
                            path: 'ref_bike_model',
                            attributes: ['name', 'cc', 'model_variation'],
                        },
                        {
                            model: 'Bike_variant',
                            path: 'ref_bike_variant',
                            attributes: ['year', 'margin'],
                        },
                    ],
                },
                {
                    model: 'Lead_status',
                    path: 'ref_lead_status',
                    attributes: ['abbr', 'name'],
                },
                {
                    model: 'Lead_customer',
                    path: 'ref_lead_customer',
                    attributes: isDeliveryRequested ? ['phone', 'full_name', 'address.post_code', 'address.county'] : ['phone', 'full_name'],
                },
                {
                    model: 'Lead_source',
                    path: 'ref_lead_source',
                    attributes: ['abbr', 'name'],
                },
                {
                    model: 'User',
                    path: 'ref_assigned_to',
                    attributes: ['person.first_name', 'person.last_name', 'person.photos.small'],
                },
                {
                    model: 'Lead_affiliate',
                    path: 'ref_lead_affiliate',
                    attributes: ['name'],
                },
                {
                    model: 'Lead_blemish_report',
                    path: 'ref_lead_blemish_report',
                    attributes: ['rag_rating'],
                },
            ],
            where,
        },
    }).then((response) => response).catch((error) => error);
};

export const getCustomersId = (customer) => api.send({
    api: 'db',
    model: 'Lead_customer',
    action: 'Read',
    options: {
        attributes: ['_id'],
        where: {
            $or: [
                {
                    phone: {
                        $regex: customer,
                        $options: 'mi',
                    },
                },
                {
                    email: {
                        $regex: customer,
                        $options: 'mi',
                    },
                },
                {
                    full_name: {
                        $regex: customer,
                        $options: 'mi',
                    },
                },
            ],
        },
    },
});

export const getCustomersPostcode = (postcode) => api.send({
    api: 'db',
    model: 'Lead_customer',
    action: 'Read',
    options: {
        attributes: ['_id'],
        where: {
            'address.post_code': {
                $regex: postcode,
                $options: 'mi',
            },
        },
    },
});

export const getBikes = (params) => {
    // possible params: vrm, bikeBrand, bikeModel, bikeVariantsId, mileageFrom, mileageTo
    let vrm;
    if (params.vrm) {
        vrm = {
            $regex: params.vrm,
            $options: 'i',
        };
    } else {
        vrm = undefined;
    }

    let mileage;
    if (params.mileageFrom !== undefined || params.mileageTo !== undefined) {
        mileage = {};
        if (params.mileageFrom !== undefined) mileage.$gte = Number(params.mileageFrom);
        if (params.mileageTo !== undefined) mileage.$lte = Number(params.mileageTo);
    }

    let bikeYear;
    if (params.yearFrom !== undefined || params.yearTo !== undefined) {
        bikeYear = {};
        if (params.yearFrom !== undefined) bikeYear.$gte = Number(params.yearFrom);
        if (params.yearTo !== undefined) bikeYear.$lte = Number(params.yearTo);
    }

    return api.send({
        api: 'db',
        model: 'Bike_lead',
        action: 'Read',
        options: {
            limit: 500,
            order: {
                _id: -1,
            },
            attributes: ['_id'],
            where: {
                vrm,
                ref_bike_brand: params.ref_bike_brand,
                ref_bike_model: params.ref_bike_model,
                ref_bike_variant: params.ref_bike_variant,
                mileage,
                'unmaped_data.bike_year': bikeYear,
            },
        },
    });
};

// is not used
export const getBikeVariantsIsHot = (is_hot) => api
    .send({
        api: 'db',
        model: 'Bike_variant',
        action: 'Read',
        options: {
            where: {
                is_hot,
            },
        },
    })
    .then((response) => response)
    .catch((error) => error);


export const getLeadsCount = (incomingFilters) => {
    // Fix perfomance
    const fixedCount = 100;

    return api
        .send({
            api: 'db',
            model: 'Lead',
            action: 'Count',
            options: {
                useAggregation: true,
                limit: fixedCount,
                where: incomingFilters,
            },
        })
        .then((response) => response)
        .catch((error) => error);
};



// WEBPACK FOOTER //
// ./src/modules/Leads/Manage_retail/requests/get_leads_request.js